/**
 * Jalali (Solar Hijri) calendar helpers.
 *
 * Time zone assumption: business dates are Asia/Tehran calendar dates. Iran
 * has used a fixed UTC+03:30 offset with no DST since 1401 (2022), so a
 * `Date` instant is mapped to its Tehran calendar day by adding 3h30m and
 * reading the UTC date. Instants before 2022 that fell in old DST periods may
 * be off by one hour near midnight; this is acceptable for this system.
 *
 * "Calendar date" values (from `fromJalali`, `jalaliQuarterStart`,
 * `tehranDay`) are `Date`s at UTC midnight of that day. Passing them back
 * into `toJalali` returns the same day.
 *
 * Conversion uses the standard 33-year-cycle break algorithm (as in
 * jalaali-js), valid for Jalali years -61 to 3177.
 */

import { toPersianDigits } from './digits';

export interface JalaliDate {
  jy: number;
  /** 1-12 */
  jm: number;
  /** 1-31 */
  jd: number;
}

export const DAY_MS = 86_400_000;
/** Asia/Tehran offset from UTC (fixed +03:30). */
export const TEHRAN_OFFSET_MS = (3 * 60 + 30) * 60_000;

export const JALALI_MONTH_NAMES = [
  'فروردین',
  'اردیبهشت',
  'خرداد',
  'تیر',
  'مرداد',
  'شهریور',
  'مهر',
  'آبان',
  'آذر',
  'دی',
  'بهمن',
  'اسفند',
] as const;

// ---------------------------------------------------------------------------
// Core algorithm (Julian Day Number based)

const BREAKS = [
  -61, 9, 38, 199, 426, 686, 756, 818, 1111, 1181, 1210, 1635, 2060, 2097, 2192, 2262, 2324, 2394, 2456, 3178,
];

const div = (a: number, b: number): number => Math.trunc(a / b);
const mod = (a: number, b: number): number => a - Math.trunc(a / b) * b;

interface JalCal {
  leap: number;
  gy: number;
  march: number;
}

function jalCal(jy: number): JalCal {
  const bl = BREAKS.length;
  const gy = jy + 621;
  let leapJ = -14;
  let jp = BREAKS[0] as number;
  if (!Number.isInteger(jy) || jy < jp || jy >= (BREAKS[bl - 1] as number)) {
    throw new RangeError(`Invalid Jalali year ${jy}`);
  }
  let jump = 0;
  for (let i = 1; i < bl; i++) {
    const jm = BREAKS[i] as number;
    jump = jm - jp;
    if (jy < jm) break;
    leapJ += div(jump, 33) * 8 + div(mod(jump, 33), 4);
    jp = jm;
  }
  let n = jy - jp;
  leapJ += div(n, 33) * 8 + div(mod(n, 33) + 3, 4);
  if (mod(jump, 33) === 4 && jump - n === 4) leapJ += 1;
  const leapG = div(gy, 4) - div((div(gy, 100) + 1) * 3, 4) - 150;
  const march = 20 + leapJ - leapG;
  if (jump - n < 6) n = n - jump + div(jump + 4, 33) * 33;
  let leap = mod(mod(n + 1, 33) - 1, 4);
  if (leap === -1) leap = 4;
  return { leap, gy, march };
}

function g2d(gy: number, gm: number, gd: number): number {
  let d =
    div((gy + div(gm - 8, 6) + 100100) * 1461, 4) + div(153 * mod(gm + 9, 12) + 2, 5) + gd - 34840408;
  d = d - div(div(gy + 100100 + div(gm - 8, 6), 100) * 3, 4) + 752;
  return d;
}

function d2g(jdn: number): { gy: number; gm: number; gd: number } {
  let j = 4 * jdn + 139361631;
  j = j + div(div(4 * jdn + 183187720, 146097) * 3, 4) * 4 - 3908;
  const i = div(mod(j, 1461), 4) * 5 + 308;
  const gd = div(mod(i, 153), 5) + 1;
  const gm = mod(div(i, 153), 12) + 1;
  const gy = div(j, 1461) - 100100 + div(8 - gm, 6);
  return { gy, gm, gd };
}

function j2d(jy: number, jm: number, jd: number): number {
  const r = jalCal(jy);
  return g2d(r.gy, 3, r.march) + (jm - 1) * 31 - div(jm, 7) * (jm - 7) + jd - 1;
}

function d2j(jdn: number): JalaliDate {
  const gy = d2g(jdn).gy;
  let jy = gy - 621;
  const r = jalCal(jy);
  let k = jdn - g2d(gy, 3, r.march);
  if (k >= 0) {
    if (k <= 185) return { jy, jm: 1 + div(k, 31), jd: mod(k, 31) + 1 };
    k -= 186;
  } else {
    jy -= 1;
    k += 179;
    if (r.leap === 1) k += 1;
  }
  return { jy, jm: 7 + div(k, 30), jd: mod(k, 30) + 1 };
}

/** Julian Day Number of 1970-01-01. */
const EPOCH_JDN = 2440588;

// ---------------------------------------------------------------------------
// Public API

/** Tehran calendar day of an instant, as a `Date` at UTC midnight. */
export function tehranDay(date: Date): Date {
  const days = Math.floor((date.getTime() + TEHRAN_OFFSET_MS) / DAY_MS);
  return new Date(days * DAY_MS);
}

function epochDayInTehran(date: Date): number {
  const t = date.getTime();
  if (Number.isNaN(t)) throw new RangeError('Invalid Date');
  return Math.floor((t + TEHRAN_OFFSET_MS) / DAY_MS);
}

/** Jalali date of the Tehran calendar day containing `date`. */
export function toJalali(date: Date): JalaliDate {
  return d2j(epochDayInTehran(date) + EPOCH_JDN);
}

/** True if the Jalali year has 366 days (Esfand has 30 days). */
export function isJalaliLeapYear(jy: number): boolean {
  return jalCal(jy).leap === 0;
}

/** Number of days in a Jalali month. */
export function jalaliMonthLength(jy: number, jm: number): number {
  if (jm <= 6) return 31;
  if (jm <= 11) return 30;
  return isJalaliLeapYear(jy) ? 30 : 29;
}

/** `Date` at UTC midnight representing the given Jalali calendar day. Throws on an invalid date. */
export function fromJalali(jy: number, jm: number, jd: number): Date {
  if (!Number.isInteger(jm) || jm < 1 || jm > 12) throw new RangeError(`Invalid Jalali month ${jm}`);
  if (!Number.isInteger(jd) || jd < 1 || jd > jalaliMonthLength(jy, jm)) {
    throw new RangeError(`Invalid Jalali day ${jy}/${jm}/${jd}`);
  }
  return new Date((j2d(jy, jm, jd) - EPOCH_JDN) * DAY_MS);
}

export interface FormatJalaliOptions {
  /** Persian digits. Default `true`. */
  persianDigits?: boolean;
  /** Append Tehran time "HH:mm". Default `false`. */
  withTime?: boolean;
}

const pad2 = (n: number): string => String(n).padStart(2, '0');

/** "۱۴۰۵/۰۷/۱۲" (or "1405/07/12", optionally with " ۱۴:۳۰" Tehran time). */
export function formatJalali(date: Date, options: FormatJalaliOptions = {}): string {
  const { jy, jm, jd } = toJalali(date);
  let out = `${jy}/${pad2(jm)}/${pad2(jd)}`;
  if (options.withTime) {
    const local = new Date(date.getTime() + TEHRAN_OFFSET_MS);
    out += ` ${pad2(local.getUTCHours())}:${pad2(local.getUTCMinutes())}`;
  }
  return (options.persianDigits ?? true) ? toPersianDigits(out) : out;
}

/** Long form with month name: "۱۲ مهر ۱۴۰۵". */
export function formatJalaliLong(date: Date, options: { persianDigits?: boolean } = {}): string {
  const { jy, jm, jd } = toJalali(date);
  const out = `${jd} ${JALALI_MONTH_NAMES[jm - 1]} ${jy}`;
  return (options.persianDigits ?? true) ? toPersianDigits(out) : out;
}

/** First day of the Jalali quarter (1 Farvardin, Tir, Mehr or Dey) containing `date`, at UTC midnight. */
export function jalaliQuarterStart(date: Date): Date {
  const { jy, jm } = toJalali(date);
  return fromJalali(jy, Math.floor((jm - 1) / 3) * 3 + 1, 1);
}

/** Receives the Tehran calendar day (UTC midnight `Date`); returns true for an official holiday. */
export type HolidayPredicate = (day: Date) => boolean;

const noHolidays: HolidayPredicate = () => false;

/** Friday is the weekly holiday. */
export function isFridayInTehran(date: Date): boolean {
  return tehranDay(date).getUTCDay() === 5;
}

/**
 * Add `n` business days to `date`. Business days are Tehran calendar days
 * that are not Friday and not `isHoliday`. The start day itself is not
 * counted. The result keeps the time of day of `date` (it is `date` plus
 * whole days). `n = 0` returns the same instant.
 */
export function addBusinessDays(date: Date, n: number, isHoliday: HolidayPredicate = noHolidays): Date {
  if (!Number.isSafeInteger(n) || n < 0) throw new RangeError(`n must be a non-negative integer, got ${n}`);
  const start = date.getTime();
  const startDay = epochDayInTehran(date);
  let k = 0;
  let counted = 0;
  while (counted < n) {
    k++;
    const day = new Date((startDay + k) * DAY_MS);
    if (day.getUTCDay() !== 5 && !isHoliday(day)) counted++;
  }
  return new Date(start + k * DAY_MS);
}

/** Add whole calendar days (Tehran has no DST, so this is exact). */
export function addCalendarDays(date: Date, days: number): Date {
  return new Date(date.getTime() + days * DAY_MS);
}

export interface EligibleAtInput {
  paidAt: Date;
  /** Calendar-day hold (spec §7.5: 15 for new sales, 7 for renewals). */
  holdDays: number;
  /** Business-day hold (spec §7.5: 7 for new sales, 0 for renewals). */
  businessDays: number;
  isHoliday?: HolidayPredicate;
}

/** `eligible_at` = the later of `paidAt + holdDays` calendar days and `paidAt + businessDays` business days. */
export function computeEligibleAt(input: EligibleAtInput): Date {
  const { paidAt, holdDays, businessDays, isHoliday } = input;
  if (!Number.isSafeInteger(holdDays) || holdDays < 0) {
    throw new RangeError(`holdDays must be a non-negative integer, got ${holdDays}`);
  }
  const byCalendar = addCalendarDays(paidAt, holdDays);
  const byBusiness = addBusinessDays(paidAt, businessDays, isHoliday);
  return byCalendar.getTime() >= byBusiness.getTime() ? byCalendar : byBusiness;
}
